// firebaseService.js
import {
  collection,
  query,
  where,
  addDoc,
  updateDoc,
  onSnapshot,
  doc,
  getDoc,
  getDocs,
  writeBatch,
  deleteDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { auth, firestore, storage } from "./firebaseConfig";
import { deleteUser } from "firebase/auth";
import { ref, deleteObject } from "firebase/storage";
import { withTrace } from "./firebasePerformance";
import { isNativeWebView } from "../nativeBridge.ts";
import { withTaskCreateTime, withTaskUpdateTime } from "../utilities/perfTraces";

// In-memory cache for invariant user metadata to avoid repeated Firestore reads
// Cache is invalidated on: user logout, account upgrade, explicit field updates

let userMetadataCache = null; // { uid, data, timestamp }

// Dev-only: Track cache reads vs Firestore reads
let userDocReadsCount = 0;
let cacheHitsCount = 0;

/**
 * Get cached user metadata (optimization #2)
 * Returns cached data if available and valid, otherwise null
 * @param {string} uid - User ID
 * @returns {Object|null} - Cached user metadata or null
 */
export function getCachedUserMeta(uid) {
  if (!uid || !userMetadataCache || userMetadataCache.uid !== uid) {
    return null;
  }

  cacheHitsCount++;
  if (process.env.NODE_ENV === "development") {
    // Only log in dev to avoid console spam
    if (cacheHitsCount % 10 === 0) {
      console.log(
        `[UserMetaCache] Cache hits: ${cacheHitsCount}, Firestore reads: ${userDocReadsCount}`
      );
    }
  }

  return userMetadataCache.data;
}

/**
 * Set cached user metadata
 * @param {string} uid - User ID
 * @param {Object} data - User metadata to cache
 */
export function setCachedUserMeta(uid, data) {
  if (!uid || !data) return;
  userMetadataCache = {
    uid,
    data: { ...data }, // Shallow copy to prevent mutations
    timestamp: Date.now(),
  };
}

/**
 * Invalidate user metadata cache
 * Call this when user logs out, upgrades account, or metadata changes
 */
export function invalidateUserMetaCache() {
  userMetadataCache = null;
}

/**
 * Get dev-only cache statistics (for verification)
 */
export function getUserMetaCacheStats() {
  return {
    cacheHits: cacheHitsCount,
    firestoreReads: userDocReadsCount,
    hasCache: userMetadataCache !== null,
    cachedUid: userMetadataCache?.uid || null,
  };
}

// Fetch tasks for a specific date, sections, and workspace
export const fetchTasks = (uid, date, workspace, callback) => {
  const q = query(
    collection(firestore, "tasks"),
    where("uid", "==", uid),
    where("workspace", "==", workspace),
    where("date", "==", date)
  );
  return onSnapshot(q, (snapshot) => {
    const tasks = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    callback(tasks);
  });
};

// Fetch tasks for a date range (recurring disabled)
export const fetchTasksForDateRange = (
  uid,
  startDate,
  endDate,
  workspace,
  callback
) => {
  const tasksQuery = query(
    collection(firestore, "tasks"),
    where("uid", "==", uid),
    where("workspace", "==", workspace),
    where("date", ">=", startDate),
    where("date", "<=", endDate)
  );

  return withTrace("fetch_tasks_for_date_range", () => {
    return onSnapshot(tasksQuery, (snapshot) => {
      const tasks = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      callback(tasks);
    });
  });
};

/**
 * Batch-migrate tasks and recurringTasks from one workspace id to another (e.g. when deleting a workspace).
 */
export const batchMigrateWorkspace = async (uid, fromWorkspaceId, toWorkspaceId) => {
  if (!uid || fromWorkspaceId === toWorkspaceId) return;

  const tasksQuery = query(
    collection(firestore, "tasks"),
    where("uid", "==", uid),
    where("workspace", "==", fromWorkspaceId)
  );
  const tasksSnap = await getDocs(tasksQuery);

  const recurringQuery = query(
    collection(firestore, "recurringTasks"),
    where("uid", "==", uid),
    where("workspace", "==", fromWorkspaceId)
  );
  const recurringSnap = await getDocs(recurringQuery);

  const allDocs = [...tasksSnap.docs, ...recurringSnap.docs];
  if (allDocs.length === 0) return;

  const BATCH_SIZE = 500;
  for (let i = 0; i < allDocs.length; i += BATCH_SIZE) {
    const batch = writeBatch(firestore);
    const chunk = allDocs.slice(i, i + BATCH_SIZE);
    chunk.forEach((d) => {
      batch.update(d.ref, { workspace: toWorkspaceId });
    });
    await batch.commit();
  }
};

/** One-shot fetch for a date range (no listener). Use for analytics "previous week" comparison. */
export const fetchTasksForDateRangeOnce = async (uid, startDate, endDate, workspace) => {
  if (!uid || !startDate || !endDate || !workspace) return [];
  const tasksQuery = query(
    collection(firestore, "tasks"),
    where("uid", "==", uid),
    where("workspace", "==", workspace),
    where("date", ">=", startDate),
    where("date", "<=", endDate)
  );
  const snapshot = await getDocs(tasksQuery);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};

// One-shot fetch for search: broad date range, no realtime listener
export const fetchTasksForSearch = async (uid, workspace) => {
  const today = new Date();
  const past = new Date(today);
  past.setDate(past.getDate() - 90);
  const future = new Date(today);
  future.setDate(future.getDate() + 30);

  const fmt = (d) => d.toISOString().slice(0, 10); // YYYY-MM-DD

  const tasksQuery = query(
    collection(firestore, "tasks"),
    where("uid", "==", uid),
    where("workspace", "==", workspace),
    where("date", ">=", fmt(past)),
    where("date", "<=", fmt(future))
  );

  const snapshot = await getDocs(tasksQuery);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};

export const addTask = async (uid, section, taskData, workspace) => {
  
  return withTaskCreateTime(async () => {
    const docRef = await addDoc(collection(firestore, "tasks"), {
      ...taskData,
      uid,
      section,
      workspace,
      date: taskData.date || new Date().toISOString().split("T")[0],
      position: taskData.position || 0,
      createdAt: serverTimestamp(),
    });
    return { id: docRef.id, ...taskData };
  });
};

export const updateTask = async (uid, taskId, updates, current = null) => {
  return withTaskUpdateTime(async () => {
    if (!taskId || !updates) return;

    const payload = {};
    for (const [k, v] of Object.entries(updates)) {
      if (v === undefined) continue;
      if (current && current[k] === v) continue;
      payload[k] = v;
    }
    if (Object.keys(payload).length === 0) return;

    await updateDoc(doc(firestore, "tasks", taskId), payload);
  });
};

/**
 * Reindex a list of tasks to ensure positions are compacted (0, 1, 2, 3...)
 * Preserves the current order of tasks in the array.
 * @param {Array} tasks - Tasks in their desired order
 * @returns {Array} Tasks with updated positions
 */
export const reindexList = (tasks) => {
  if (!Array.isArray(tasks)) return [];
  return tasks.map((task, index) => ({
    ...task,
    position: index,
  }));
};

/**
 * Sanity check for position gaps or duplicates (dev-only warning)
 * @param {Array} tasks - Tasks to check
 * @param {string} section - Section name for error message
 */
export const checkPositionSanity = (tasks, section) => {
  if (process.env.NODE_ENV !== "development") return;

  if (!Array.isArray(tasks) || tasks.length === 0) return;

  const positions = tasks.map((t) => t.position ?? -1).sort((a, b) => a - b);
  const hasGaps = positions.some((pos, idx) => pos !== idx);
  const hasDuplicates = new Set(positions).size !== positions.length;

  if (hasGaps || hasDuplicates) {
    console.warn(`[Position Sanity Check] ${section} section has issues:`, {
      hasGaps,
      hasDuplicates,
      positions,
      taskCount: tasks.length,
    });
  }
};

// Batch update task positions
// Note: Virtual tasks (those with IDs starting with "virtual:") must be materialized
// before calling this function. This function only updates existing Firestore documents.
export const batchUpdateTaskPositions = async (
  uid,
  section,
  tasks,
  workspace
) => {
  try {
    const batch = writeBatch(firestore);
    const realTasks = tasks.filter((task) => {
      // Only update real Firestore tasks, skip virtual tasks
      return task.id && !task.id.startsWith("virtual:");
    });

    // Reindex before updating to ensure positions are compacted
    const reindexedTasks = reindexList(realTasks);

    reindexedTasks.forEach((task) => {
      const taskRef = doc(firestore, "tasks", task.id);
      batch.update(taskRef, { position: task.position, section, workspace });
    });

    if (reindexedTasks.length > 0) {
      await batch.commit();
    }
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

// Delete a task
export const deleteTask = async (uid, taskId) => {
  return withTrace("delete_task_trace", async () => {
    const taskRef = doc(firestore, "tasks", taskId);
    await deleteDoc(taskRef);
  });
};

// Move a task
export const handleTaskMove = async (uid, task, newSection, workspace) => {
  return withTrace("move_task_trace", async () => {
    await updateTask(uid, task.id, { section: newSection, workspace });
  });
};

// ============================================================================
// OLD RECURRING LOGIC - DISABLED (kept for reference, will be removed)
// ============================================================================
// These functions are disabled and will be removed. They materialized tasks
// instantly which caused performance issues and broke the materialization model.

// const expandRecurringRules = async (...) => { ... } // DISABLED
// const materializeVirtualTaskInstantly = async (...) => { ... } // DISABLED
// export const materializeVirtualTask = async (...) => { ... } // DISABLED - replaced below
// export const deleteVirtualRecurringSource = async (...) => { ... } // DISABLED - replaced below

// ============================================================================
// NEW RECURRING TASKS SYSTEM
// ============================================================================
// Recurring templates are stored in a separate collection.
// Virtual instances are created in memory and only materialized on interaction.

// Fetch all recurring templates for a workspace (cached, not per-date)
export const fetchRecurringTemplates = (uid, workspace, callback) => {
  const q = query(
    collection(firestore, "recurringTasks"),
    where("uid", "==", uid),
    where("workspace", "==", workspace)
  );
  return onSnapshot(q, (snapshot) => {
    const templates = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    callback(templates);
  });
};

// Add a recurring template
export const addRecurringTemplate = async (uid, workspace, templateData) => {
  return withTrace("add_recurring_template", async () => {
    const now = new Date().toISOString();
    const docRef = await addDoc(collection(firestore, "recurringTasks"), {
      ...templateData,
      uid,
      workspace,
      weekdays: templateData.weekdays || [], // array of 0-6
      createdAt: now,
      updatedAt: now,
    });
    return { id: docRef.id, ...templateData };
  });
};

// Update a recurring template
export const updateRecurringTemplate = async (uid, templateId, updates) => {
  return withTrace("update_recurring_template", async () => {
    if (!templateId || !updates) return;

    const payload = {
      ...updates,
      updatedAt: new Date().toISOString(),
    };
    // Remove undefined values
    Object.keys(payload).forEach((key) => {
      if (payload[key] === undefined) delete payload[key];
    });

    if (Object.keys(payload).length === 0) return;

    await updateDoc(doc(firestore, "recurringTasks", templateId), payload);
  });
};

// Delete a recurring template
export const deleteRecurringTemplate = async (uid, templateId) => {
  return withTrace("delete_recurring_template", async () => {
    const templateRef = doc(firestore, "recurringTasks", templateId);
    await deleteDoc(templateRef);
  });
};

// Get a single recurring template by ID
export const getRecurringTemplate = async (uid, templateId) => {
  return withTrace("get_recurring_template", async () => {
    const templateRef = doc(firestore, "recurringTasks", templateId);
    const templateSnap = await getDoc(templateRef);

    if (!templateSnap.exists()) {
      return null;
    }

    const data = templateSnap.data();
    // Verify ownership
    if (data.uid !== uid) {
      return null;
    }

    return { id: templateSnap.id, ...data };
  });
};

// Materialize a virtual recurring task instance into a real task document
// This is called when the user meaningfully interacts with a virtual task
export const materializeRecurringInstance = async (
  uid,
  templateId,
  instanceDate,
  templateData,
  section,
  workspace
) => {
  return withTrace("materialize_recurring_instance", async () => {
    // Check if instance already exists using deterministic occurrence ID
    // Format: recurring:{templateId}:{YYYY-MM-DD}
    const occurrenceId = `recurring:${templateId}:${instanceDate}`;

    const existingQuery = query(
      collection(firestore, "tasks"),
      where("uid", "==", uid),
      where("workspace", "==", workspace),
      where("date", "==", instanceDate),
      where("recurringOccurrenceId", "==", occurrenceId)
    );
    const existingSnap = await getDocs(existingQuery);

    if (!existingSnap.empty) {
      // Instance already exists, return it
      const existingDoc = existingSnap.docs[0];
      const existingData = existingDoc.data();
      return { id: existingDoc.id, ...existingData };
    }

    // Fallback: Check by title and date (for backwards compatibility)
    const fallbackQuery = query(
      collection(firestore, "tasks"),
      where("uid", "==", uid),
      where("workspace", "==", workspace),
      where("date", "==", instanceDate)
    );
    const fallbackSnap = await getDocs(fallbackQuery);
    const matchingTask = fallbackSnap.docs.find(
      (doc) => doc.data().title === (templateData.title || "Untitled Task")
    );

    if (matchingTask) {
      // Instance already exists, return it
      const existingData = matchingTask.data();
      return { id: matchingTask.id, ...existingData };
    }

    // Create new materialized instance
    // Store recurringTemplateId and occurrenceId for proper cleanup
    const newTask = {
      title: templateData.title || "Untitled Task",
      section: section || templateData.section || "todo",
      workspace,
      uid,
      date: instanceDate,
      priority: templateData.priority || "Medium",
      status: templateData.status || "todo",
      description: templateData.description ?? "",
      links: templateData.links || [],
      subtasks: templateData.subtasks || [],
      categories: templateData.categories || [],
      // Store recurring fields for proper series management
      recurringTemplateId: templateId,
      recurringOccurrenceId: occurrenceId, // Deterministic ID: recurring:{templateId}:{date}
      isMaterializedInstance: true,
      position: 0,
      createdAt: serverTimestamp(),
    };

    const docRef = await addDoc(collection(firestore, "tasks"), newTask);
    return { id: docRef.id, ...newTask };
  });
};

// Delete all future materialized instances for a recurring template
export const deleteFutureMaterializedInstances = async (
  uid,
  templateId,
  parentTaskDate,
  workspace
) => {
  return withTrace("delete_future_materialized_instances", async () => {
    // Query all tasks with this recurringTemplateId that are materialized instances
    // and have dates after the parent task date
    const instancesQuery = query(
      collection(firestore, "tasks"),
      where("uid", "==", uid),
      where("workspace", "==", workspace),
      where("recurringTemplateId", "==", templateId),
      where("isMaterializedInstance", "==", true)
    );

    const instancesSnap = await getDocs(instancesQuery);
    const batch = writeBatch(firestore);
    let deleteCount = 0;

    instancesSnap.docs.forEach((docSnap) => {
      const taskData = docSnap.data();
      // Only delete instances with dates after the parent task date
      if (taskData.date > parentTaskDate) {
        batch.delete(docSnap.ref);
        deleteCount++;
      }
    });

    if (deleteCount > 0) {
      await batch.commit();
    }

    return deleteCount;
  });
};

export async function deleteUserAccount() {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("No authenticated user.");
  }

  const uid = user.uid;

  // 1. Delete all tasks for this user
  const tasksQuery = query(
    collection(firestore, "tasks"),
    where("uid", "==", uid)
  );
  const tasksSnap = await getDocs(tasksQuery);
  const taskDeletes = tasksSnap.docs.map((docSnap) => deleteDoc(docSnap.ref));
  await Promise.all(taskDeletes);

  // 2. Delete all file metadata + storage objects for this user
  const filesQuery = query(
    collection(firestore, "files"),
    where("uid", "==", uid)
  );
  const filesSnap = await getDocs(filesQuery);

  const fileOps = filesSnap.docs.map(async (fileDocSnap) => {
    const fileData = fileDocSnap.data();
    const storagePath = fileData.storagePath;

    if (storagePath) {
      try {
        const objectRef = ref(storage, storagePath);
        await deleteObject(objectRef);
      } catch (err) {
        console.warn(
          "Error deleting storage object for file",
          fileDocSnap.id,
          err
        );
      }
    }

    await deleteDoc(fileDocSnap.ref);
  });

  await Promise.all(fileOps);

  // 3. Finally delete Auth user
  await deleteUser(user);
}

/**
 * Ensure a users/{uid} document exists in Firestore with minimal profile data.
 * Creates the document if it doesn't exist, updates lastSeenAt if it does.
 * Keeps writes minimal:
 * - createdAt only set if doc doesn't exist (write-once)
 * - lastSeenAt only updated if missing OR older than 24 hours (throttled)
 *
 * @param {string} uid - User ID
 * @param {Object} additionalData - Optional additional data to merge (e.g., accountType, platformFirstSeen)
 */
export async function ensureUserDoc(uid, additionalData = {}) {
  if (!uid) return;

  try {
    const userRef = doc(firestore, "users", uid);
    const userSnap = await getDoc(userRef);

    userDocReadsCount++; // Track Firestore reads (dev only)

    const now = serverTimestamp();
    const platformFirstSeen = isNativeWebView() ? "ios" : "web";

    if (!userSnap.exists()) {
      // Document doesn't exist - create it with createdAt
      const acct = additionalData.accountType || "guest";

      const newData = {
        createdAt: now,
        lastSeenAt: now,
        platformFirstSeen,
        accountType: acct,

        // Backwards-compat for any older logic that still checks isAnonymous
        isAnonymous: acct !== "registered",

        ...additionalData,
      };

      await setDoc(userRef, newData, { merge: true });

      // Cache the newly created doc metadata (optimization #2)
      // Note: serverTimestamp() becomes a placeholder, but we cache what we can
      setCachedUserMeta(uid, {
        accountType: acct,
        platformFirstSeen,
        isAnonymous: acct !== "registered",
        ...additionalData,
      });
    } else {
      // Document exists - check if we need to update lastSeenAt
      const existingData = userSnap.data();
      const updateData = {};

      // Only update lastSeenAt if it's missing OR older than 24 hours
      const existingLastSeenAt = existingData.lastSeenAt;
      let shouldUpdateLastSeenAt = false;

      if (!existingLastSeenAt) {
        shouldUpdateLastSeenAt = true;
      } else {
        let lastSeenDate = null;
        if (existingLastSeenAt?.toDate) {
          lastSeenDate = existingLastSeenAt.toDate();
        } else if (existingLastSeenAt?.seconds) {
          lastSeenDate = new Date(existingLastSeenAt.seconds * 1000);
        } else if (existingLastSeenAt instanceof Date) {
          lastSeenDate = existingLastSeenAt;
        } else if (
          typeof existingLastSeenAt === "string" ||
          typeof existingLastSeenAt === "number"
        ) {
          lastSeenDate = new Date(existingLastSeenAt);
        }

        if (lastSeenDate) {
          const hoursSinceLastSeen =
            (Date.now() - lastSeenDate.getTime()) / (1000 * 60 * 60);
          shouldUpdateLastSeenAt = hoursSinceLastSeen >= 24;
        } else {
          shouldUpdateLastSeenAt = true;
        }
      }

      if (shouldUpdateLastSeenAt) {
        updateData.lastSeenAt = now;
      }

      // Only update accountType if it's not already set or if explicitly provided
      if (additionalData.accountType && !existingData.accountType) {
        updateData.accountType = additionalData.accountType;
        updateData.isAnonymous = additionalData.accountType !== "registered";
      }

      // Only set isAnonymous if missing (backfill for legacy docs)
      if (existingData.isAnonymous === undefined && existingData.accountType) {
        updateData.isAnonymous = existingData.accountType !== "registered";
      }

      // Only update platformFirstSeen if it's not already set
      if (!existingData.platformFirstSeen) {
        updateData.platformFirstSeen = platformFirstSeen;
      }

      // Only write if there's something to update
      if (Object.keys(updateData).length > 0) {
        await updateDoc(userRef, updateData);

        // Update cache with new values (optimization #2)
        const cached = getCachedUserMeta(uid) || {};
        setCachedUserMeta(uid, {
          ...cached,
          ...updateData,
        });
      }

      // Cache all invariant metadata from the read (optimization #2)
      // Extract fields we want to cache (exclude lastSeenAt which changes frequently)
      const cacheableData = {
        accountType:
          existingData.accountType || additionalData.accountType || "guest",
        platformFirstSeen: existingData.platformFirstSeen || platformFirstSeen,
        createdAt: existingData.createdAt,

        // Backwards-compat
        isAnonymous:
          existingData.isAnonymous ??
          (existingData.accountType
            ? existingData.accountType !== "registered"
            : (additionalData.accountType || "guest") !== "registered"),

        // Subscription preferences
        subscription: existingData.subscription
          ? {
              preferredPlan: existingData.subscription.preferredPlan,
            }
          : undefined,

        // Signup prompt flags
        hasSeenSignupSoftPrompt: existingData.hasSeenSignupSoftPrompt,
        signupPromptDismissed: existingData.signupPromptDismissed,
        signupBannerDismissed: existingData.signupBannerDismissed,

        // Conversion timestamp
        upgradedAt: existingData.upgradedAt,
      };

      setCachedUserMeta(uid, cacheableData);
    }
  } catch (error) {
    console.error("Failed to ensure user doc:", error);
    // Don't throw - this is non-critical for app functionality
  }
}

/**
 * Get signup prompt status (optimization #2: uses cache, falls back to Firestore)
 * @param {string} uid - User ID
 * @returns {Promise<{hasSeen: boolean, isDismissed: boolean}>} - Both flags from cache or single read
 */
export async function getSignupPromptStatus(uid) {
  if (!uid) return { hasSeen: false, isDismissed: false };

  // Try cache first (optimization #2)
  const cached = getCachedUserMeta(uid);
  if (cached) {
    return {
      hasSeen: cached.hasSeenSignupSoftPrompt === true,
      isDismissed: cached.signupPromptDismissed === true,
    };
  }

  // Cache miss - fallback to Firestore read
  try {
    const userRef = doc(firestore, "users", uid);
    const userSnap = await getDoc(userRef);

    userDocReadsCount++; // Track Firestore reads (dev only)

    if (!userSnap.exists()) {
      return { hasSeen: false, isDismissed: false };
    }

    const userData = userSnap.data();
    const result = {
      hasSeen: userData.hasSeenSignupSoftPrompt === true,
      isDismissed: userData.signupPromptDismissed === true,
    };

    // Cache the data for future reads (optimization #2)
    const cacheableData = {
      hasSeenSignupSoftPrompt: userData.hasSeenSignupSoftPrompt,
      signupPromptDismissed: userData.signupPromptDismissed,
      signupBannerDismissed: userData.signupBannerDismissed,
      accountType: userData.accountType,
      platformFirstSeen: userData.platformFirstSeen,
      subscription: userData.subscription,
      upgradedAt: userData.upgradedAt,
      isAnonymous: userData.isAnonymous,
    };
    setCachedUserMeta(uid, cacheableData);

    return result;
  } catch (error) {
    console.error("Failed to get signup prompt status:", error);
    return { hasSeen: false, isDismissed: false };
  }
}

/**
 * Check if user has seen the signup soft prompt
 * @deprecated Use getSignupPromptStatus instead (optimization #2)
 */
export async function hasSeenSignupSoftPrompt(uid) {
  if (!uid) return false;

  try {
    const userRef = doc(firestore, "users", uid);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) return false;

    const userData = userSnap.data();
    return userData.hasSeenSignupSoftPrompt === true;
  } catch (error) {
    console.error("Failed to check signup prompt status:", error);
    return false;
  }
}

/**
 * Check if user has dismissed the signup prompt
 * @deprecated Use getSignupPromptStatus instead (optimization #2)
 */
export async function isSignupPromptDismissed(uid) {
  if (!uid) return false;

  try {
    const userRef = doc(firestore, "users", uid);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) return false;

    const userData = userSnap.data();
    return userData.signupPromptDismissed === true;
  } catch (error) {
    console.error("Failed to check signup prompt dismissal:", error);
    return false;
  }
}

/**
 * Mark that user has seen the signup soft prompt
 * @param {string} uid - User ID
 * @param {Function} onCacheInvalidate - Optional callback to invalidate cache after write (deprecated, cache updated automatically)
 */
export async function markSignupSoftPromptSeen(uid, onCacheInvalidate = null) {
  if (!uid) return;

  try {
    const userRef = doc(firestore, "users", uid);
    await updateDoc(userRef, {
      hasSeenSignupSoftPrompt: true,
    });

    // Update cache immediately (optimization #2)
    const cached = getCachedUserMeta(uid) || {};
    setCachedUserMeta(uid, {
      ...cached,
      hasSeenSignupSoftPrompt: true,
    });

    // Legacy callback support (for MainPage compatibility)
    if (onCacheInvalidate) {
      onCacheInvalidate();
    }
  } catch (error) {
    console.error("Failed to mark signup prompt as seen:", error);
  }
}

/**
 * Dismiss the signup prompt (user clicked "Not now")
 * @param {string} uid - User ID
 * @param {Function} onCacheInvalidate - Optional callback to invalidate cache after write (deprecated, cache updated automatically)
 */
export async function dismissSignupPrompt(uid, onCacheInvalidate = null) {
  if (!uid) return;

  try {
    const userRef = doc(firestore, "users", uid);
    await updateDoc(userRef, {
      signupPromptDismissed: true,
    });

    // Update cache immediately (optimization #2)
    const cached = getCachedUserMeta(uid) || {};
    setCachedUserMeta(uid, {
      ...cached,
      signupPromptDismissed: true,
    });

    // Legacy callback support (for MainPage compatibility)
    if (onCacheInvalidate) {
      onCacheInvalidate();
    }
  } catch (error) {
    console.error("Failed to dismiss signup prompt:", error);
  }
}

/**
 * Update user document after successful account upgrade (linking credentials to anonymous user)
 * This is the canonical "conversion event" write.
 *
 * Key goals:
 * - Always record upgradedAt (conversion timestamp)
 * - Never fail if the users/{uid} doc doesn't exist yet
 * - Update in-memory cache so UI/admin views are consistent immediately
 *
 * @param {string} uid - User ID (should be the same as before linking)
 */
export async function updateUserAfterUpgrade(uid) {
  if (!uid) return;

  try {
    const userRef = doc(firestore, "users", uid);

    // Use setDoc + merge so this NEVER fails if the doc doesn't exist
    await setDoc(
      userRef,
      {
        accountType: "registered",
        upgradedAt: serverTimestamp(),

        // Optional: keeps your banner logic consistent immediately
        signupBannerDismissed: true,

        // Optional: legacy / fallback fields some parts of UI may rely on
        isAnonymous: false,
      },
      { merge: true }
    );

    // Update cache immediately (optimization #2)
    const cached = getCachedUserMeta(uid) || {};
    setCachedUserMeta(uid, {
      ...cached,
      accountType: "registered",
      upgradedAt: cached.upgradedAt || true, // presence hint; Firestore stores actual Timestamp
      signupBannerDismissed: true,
      isAnonymous: false,
    });
  } catch (error) {
    console.error("Failed to update user after upgrade:", error);
  }
}
